// src/features/records/services/core/queries.js

import { auth, db } from '../../../../firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { LIFECYCLE_STATUS } from '../../constants';
import { normalizeRecord, normalizeRecordSnapshot } from '../utils/normalizeRecord';

/**
 * Subscribe to the record document only (no attachments).
 */
export function subscribeToRecord(recordId, onData, onError) {
  if (!auth.currentUser || !recordId) {
    console.error('subscribeToRecord: requires auth and recordId');
    return () => {};
  }

  const recordRef = doc(db, 'records', recordId);
  return onSnapshot(
    recordRef,
    (snap) => {
      if (!snap.exists()) return onData(null);
      const data = normalizeRecordSnapshot(snap);

      if (data?.lifecycle?.status === LIFECYCLE_STATUS.DELETED) {
        return onData(null);
      }

      onData(data);
    },
    (err) => {
      console.error('Error in record doc subscription:', err);
      onError?.(err);
      onData(null);
    }
  );
}

/**
 * Fetch records by IDs preserving order.
 * Used exclusively for edge-index hydration; lists must source IDs from edges.
 * Uses per-document reads to honor record-level access checks.
 */
export async function fetchRecordsByIds(ids, viewFlagKey = null) {
  if (!ids || ids.length === 0) return [];

  const records = [];

  const fetchOne = async (id) => {
    try {
      const docSnap = await getDoc(doc(db, 'records', id));
      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      if (!data || data?.lifecycle?.status !== LIFECYCLE_STATUS.ACTIVE) {
        return null;
      }

      if (viewFlagKey && docSnap.get(viewFlagKey) !== true) {
        return null;
      }

      return normalizeRecord({ id: docSnap.id, ...data });
    } catch (error) {
      if (error?.code && (error.code === 'permission-denied' || error.code === 'not-found')) {
        console.warn('fetchRecordsByIds: skipping inaccessible record', { id, code: error.code });
        return null;
      }
      throw error;
    }
  };

  for (let i = 0; i < ids.length; i += 10) {
    const batch = ids.slice(i, i + 10);
    const results = await Promise.all(batch.map((id) => fetchOne(id)));

    results.forEach((record) => {
      if (record) {
        records.push(record);
      }
    });
  }

  return records;
}
